/**
 * 👁️ AdminBosses — owner-only boss stat editor.
 *
 * Live-edit any boss's hp_mult/atk_mult/def_mult/coins/pt_reward/start_hour
 * and the override saves to mongo immediately. Fights pick up the new
 * values on the next /api/arena/start (no redeploy needed).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fields rendered in the editor — type drives input control
const FIELDS = [
  { key: "hp_mult",       label: "HP Mult",      type: "number", step: 0.1 },
  { key: "atk_mult",      label: "ATK Mult",     type: "number", step: 0.05 },
  { key: "def_mult",      label: "DEF Mult",     type: "number", step: 0.05 },
  { key: "difficulty",    label: "Difficulty",   type: "number", step: 1, min: 1, max: 5 },
  { key: "coins",         label: "Win Coins",    type: "number", step: 10 },
  { key: "pt_reward",     label: "Phoenix Tokens Reward",    type: "number", step: 10 },
  { key: "start_hour",    label: "Start Hour (PT)",  type: "number", step: 1, min: 0, max: 23 },
  { key: "weekend_only",  label: "Weekend Only", type: "boolean" },
  { key: "personality",   label: "Personality",  type: "select",
    options: ["aggressive", "balanced", "defensive", "stalker", "fury", "wraith", "cocky"] },
  { key: "name",          label: "Name",         type: "text" },
  { key: "subtitle",      label: "Subtitle",     type: "text" },
  { key: "tagline",       label: "Tagline",      type: "text" },
  { key: "color",         label: "Color",        type: "text" },
];

export default function AdminBosses() {
  const { user, loading: authLoading } = useAuth();
  const [bosses, setBosses] = useState(null);
  const [edits, setEdits] = useState({}); // { boss_id: { field: value } }
  const [busy, setBusy] = useState(false);

  const isOwner = user && (user.role === "owner" || (user.email || "").toLowerCase() === "omnimuscle@yahoo.com");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/bosses`);
      setBosses(r.data.bosses);
      setEdits({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load bosses.");
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isOwner) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isOwner]);

  if (authLoading) return null;
  if (!isOwner) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400" data-testid="admin-bosses-locked">
        🔒 Owner only.
      </div>
    );
  }
  if (!bosses) {
    return <div className="max-w-3xl mx-auto p-8 text-center text-neutral-500">Loading…</div>;
  }

  const change = (bossId, field, value) => {
    setEdits((prev) => ({ ...prev, [bossId]: { ...(prev[bossId] || {}), [field]: value } }));
  };

  const save = async (bossId) => {
    const payload = edits[bossId];
    if (!payload || !Object.keys(payload).length) return;
    setBusy(true);
    try {
      await axios.put(`${API}/admin/bosses/${bossId}`, payload);
      toast.success(`✅ ${bossId} updated`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
    setBusy(false);
  };

  const reset = async (bossId) => {
    if (!window.confirm(`Reset ${bossId} to code defaults?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/bosses/${bossId}`);
      toast.success(`🔄 ${bossId} reset`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8" data-testid="admin-bosses-page">
      <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
        <a href="/admin/heroes" className="hover:text-white">🦸 Heroes</a>
        <span className="text-neutral-700">·</span>
        <a href="/admin/bosses" className="text-[#FCD34D] font-bold">👁️ Bosses</a>
      </div>
      <h1 className="font-display text-3xl mb-2">👁️ Boss Stat Editor</h1>
      <p className="text-xs text-neutral-500 mb-6">
        Live-edit any boss. Saves go straight to mongo — no redeploy. Fights pick up new values on the next /arena/start.
        Active raids keep their current pool until next window.
      </p>

      <div className="space-y-4">
        {bosses.map((b) => {
          const e = edits[b.id] || {};
          return (
            <div
              key={b.id}
              className={`border p-3 ${b.is_overridden ? "border-[#FCD34D] bg-amber-950/10" : "border-neutral-800 bg-neutral-950"}`}
              data-testid={`admin-boss-${b.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{b.icon}</span>
                  <div>
                    <div className="font-display text-lg">{e.name ?? b.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      {b.id} · {b.start_hour != null ? `${b.start_hour}:00 PT · ${b.weekend_only ? "wknd" : "daily"}` : "always-on"}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {b.is_overridden && (
                    <span className="text-[9px] uppercase tracking-widest text-[#FCD34D] self-center mr-2" title={b.overridden_fields.join(", ")}>
                      🟡 Overridden ({b.overridden_fields.length})
                    </span>
                  )}
                  <button
                    onClick={() => save(b.id)}
                    disabled={busy || !Object.keys(e).length}
                    data-testid={`admin-save-${b.id}`}
                    className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#D4AF37] hover:bg-[#F1C84B] text-black disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Save
                  </button>
                  {b.is_overridden && (
                    <button
                      onClick={() => reset(b.id)}
                      disabled={busy}
                      data-testid={`admin-reset-${b.id}`}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-neutral-800 hover:bg-neutral-700 text-neutral-300"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {FIELDS.map((f) => (
                  <FieldInput
                    key={f.key}
                    field={f}
                    value={e[f.key] !== undefined ? e[f.key] : b[f.key]}
                    isEdited={e[f.key] !== undefined}
                    onChange={(v) => change(b.id, f.key, v)}
                    testId={`admin-${b.id}-${f.key}`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FieldInput({ field, value, isEdited, onChange, testId }) {
  const base = "w-full bg-black border px-2 py-1.5 text-xs text-white";
  const borderCls = isEdited ? "border-[#FCD34D]" : "border-neutral-800";
  if (field.type === "boolean") {
    return (
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-widest text-neutral-500">{field.label}</span>
        <select
          value={String(!!value)}
          onChange={(ev) => onChange(ev.target.value === "true")}
          data-testid={testId}
          className={`${base} ${borderCls}`}
        >
          <option value="false">No</option>
          <option value="true">Yes</option>
        </select>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-widest text-neutral-500">{field.label}</span>
        <select
          value={value || ""}
          onChange={(ev) => onChange(ev.target.value)}
          data-testid={testId}
          className={`${base} ${borderCls}`}
        >
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[9px] uppercase tracking-widest text-neutral-500">{field.label}</span>
      <input
        type={field.type}
        step={field.step}
        min={field.min}
        max={field.max}
        value={value ?? ""}
        onChange={(ev) => onChange(field.type === "number" ? (ev.target.value === "" ? null : Number(ev.target.value)) : ev.target.value)}
        data-testid={testId}
        className={`${base} ${borderCls} font-mono tabular-nums`}
      />
    </label>
  );
}
